import { useState } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import LzSApp from '@lz-app/App';
import useLzFApiCall from "@lz-services/Api";
import { useLzOnMount } from "@lz-services/Hooks";
import { lzFDeepVarSet } from "@lz-services/Type";
import { lzFShowSuccessMessage } from "@lz-services/Utils";
import LzXForm from "@lz-components/forms/Form";
import LzXInputField from '@lz-components/forms/fields/InputField';
import ConfigurationsModel from "./configurations.model";

import './ConfigurationsScreen.scss';
import LzXSwitchField from "@lz/components/forms/fields/SwitchField";

const ConfigurationsScreen = () => {
    const [configs, setConfigs] = useState(new ConfigurationsModel());
    const lzApiCall = useLzFApiCall();

    const load = async () => {
        const data = await lzApiCall('app-config');

        if (data && data.config) {
            setConfigs(new ConfigurationsModel(data.config));
        }
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        const data = await lzApiCall('app-config', { configs: configs }, 'post');
        if (data) {
            lzFShowSuccessMessage('Configurations updated successfully!');
        }
    };

    const handleInputChange = (key, value) => {
        const updated = Object.assign({}, configs);
        lzFDeepVarSet(updated, key, value);
        setConfigs(updated);
    };

    // Call only for the first time when component is initially loaded
    useLzOnMount(() => {
        load();
    });

    return (
        <LzSApp.WrapperTemplate private screenTitle="Configurations">
            <div className={'lz-form-page lz-form-page-full lz-form-page-no-top-margin'}>

                <LzXForm
                    submitLabel="Save Changes"
                    onSubmit={handleFormSubmit}
                    cancelLabel="Reload"
                    onCancel={load}
                >
                    <div className="heading">
                        <Typography variant="overline">Company Information</Typography>
                    </div>
                    <Grid container spacing={2}>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Company Name"
                                name="company.name" value={configs.company.name} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Company Address"
                                name="company.address" value={configs.company.address} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Company Province"
                                name="company.province" value={configs.company.province} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Phone #s"
                                name="company.phone_nos" value={configs.company.phone_nos} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Fax #s"
                                name="company.fax_nos" value={configs.company.fax_nos} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Email Address"
                                name="company.email" value={configs.company.email} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Sales Tax Reg. No."
                                name="company.strn" value={configs.company.strn} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="NTN No."
                                name="company.ntn" value={configs.company.ntn} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Company Slogan"
                                name="company.slogan" value={configs.company.slogan} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Company Url"
                                name="company.url" value={configs.company.url} onChange={handleInputChange} />
                        </Grid>
                    </Grid>
                    <br />

                    <div className="heading">
                        <Typography variant="overline">Head Office Information</Typography>
                    </div>
                    <Grid container spacing={2}>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Head Office Address"
                                name="company.head_office.address" value={configs.company.head_office.address} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Head Office Phone #s"
                                name="company.head_office.phone_nos" value={configs.company.head_office.phone_nos} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Head Office Fax #s"
                                name="company.head_office.fax_nos" value={configs.company.head_office.fax_nos} onChange={handleInputChange} />
                        </Grid>
                    </Grid>
                    <br />

                    <div className="heading">
                        <Typography variant="overline">Miscellaneous Settings</Typography>
                    </div>
                    <Grid container spacing={2}>
                        <Grid size={{ sm: 3 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Default Item HS Code"
                                name="misc.default_item_hs_code" value={configs.misc.default_item_hs_code} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 3 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Current Standard Tax Rate (%)"
                                name="misc.current_standard_tax_rate" value={configs.misc.current_standard_tax_rate}
                                onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 3 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Current Further Tax Rate (%)"
                                name="misc.current_further_tax_rate" value={configs.misc.current_further_tax_rate}
                                onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 3 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Default Discount Rate (%)"
                                name="misc.default_discount_rate" value={configs.misc.default_discount_rate}
                                onChange={handleInputChange} />
                        </Grid>
                    </Grid>
                    <br />

                    <div className="heading">
                        <Typography variant="overline">FBR Sales Invoicing Digital Invoicing API Settings</Typography>
                    </div>
                    <Grid container spacing={2}>
                        <Grid size={{ sm: 6 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Sandbox Security Token"
                                name="fbr_sidi_api.sandbox_security_token" value={configs.fbr_sidi_api.sandbox_security_token} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 6 }}>
                            <LzXInputField fluid labelShrinked variant="outlined" label="Production Security Token"
                                name="fbr_sidi_api.production_security_token" value={configs.fbr_sidi_api.production_security_token} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXSwitchField fluid label="Is Sandbox Enabled" name="fbr_sidi_api.is_sandbox_enabled"
                                value={configs.fbr_sidi_api.is_sandbox_enabled} onChange={handleInputChange} />
                        </Grid>
                        <Grid size={{ sm: 4 }}>
                            <LzXSwitchField fluid label="Is Debugging Enabled" name="fbr_sidi_api.is_debug_enabled"
                                value={configs.fbr_sidi_api.is_debug_enabled} onChange={handleInputChange} />
                        </Grid>
                    </Grid>
                    <br />

                </LzXForm>

            </div>
        </LzSApp.WrapperTemplate>
    );

};

export default ConfigurationsScreen;
